import React from 'react';

import { CameraAngle, CameraMove } from '../studio-contract';

/**
 * Line drawings of where the camera stands and how it moves: a floor, an
 * armchair, a camera and what it sees (a softly filled cone). Grey on the
 * tile, the way the rest of the app draws grouped choices.
 *
 * Moves are drawn with their path as an arrow, and the camera travels along
 * it while the tile is hovered or chosen (CSS, in `GLYPH_STYLES`).
 */

const W = 96;
const H = 64;
const FLOOR_Y = 54;

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

/** An armchair seen from the front-side, bottom at (x, FLOOR_Y). `s` scales it. */
function Chair({ x, s = 1 }: { x: number; s?: number }) {
    const y = FLOOR_Y;
    return (
        <g transform={`translate(${x} ${y}) scale(${s}) translate(0 -26)`} {...stroke}>
            {/* back */}
            <path d="M5 0 h18 a3 3 0 0 1 3 3 v11 h-24 v-11 a3 3 0 0 1 3 -3 z" />
            {/* seat */}
            <path d="M1 14 h26 a2 2 0 0 1 2 2 v3 h-30 v-3 a2 2 0 0 1 2 -2 z" />
            {/* arms */}
            <path d="M-1 10 v9 M29 10 v9" />
            {/* legs */}
            <path d="M2 19 v7 M26 19 v7" />
        </g>
    );
}

/** A camera body with its lens pointing along `angle` degrees (0 = right). */
function Cam({ x, y, angle, cone = 26, spread = 15 }: { x: number; y: number; angle: number; cone?: number; spread?: number }) {
    const a = (angle * Math.PI) / 180;
    const s = (spread * Math.PI) / 180;
    const lx = x + Math.cos(a) * 6;
    const ly = y + Math.sin(a) * 6;
    const p1 = [lx + Math.cos(a - s) * cone, ly + Math.sin(a - s) * cone];
    const p2 = [lx + Math.cos(a + s) * cone, ly + Math.sin(a + s) * cone];
    return (
        <g className="cam">
            <path d={`M${lx} ${ly} L${p1[0]} ${p1[1]} L${p2[0]} ${p2[1]} Z`} fill="currentColor" opacity={0.09} stroke="none" />
            <path d={`M${lx} ${ly} L${p1[0]} ${p1[1]} M${lx} ${ly} L${p2[0]} ${p2[1]}`} {...stroke} strokeWidth={1} strokeDasharray="2.5 2.5" opacity={0.55} />
            <g transform={`translate(${x} ${y}) rotate(${angle})`} {...stroke}>
                <rect x={-8} y={-5} width={12} height={10} rx={2.5} fill="var(--glyph-bg, white)" />
                <path d="M4 -3 L8 -5 L8 5 L4 3 Z" fill="var(--glyph-bg, white)" />
                <circle cx={-2} cy={0} r={2} />
            </g>
        </g>
    );
}

const Floor = () => <line x1={6} y1={FLOOR_Y} x2={90} y2={FLOOR_Y} {...stroke} strokeWidth={1.2} opacity={0.45} />;

function Arrow({ d }: { d: string }) {
    return (
        <g className="path" {...stroke} strokeWidth={1.3} opacity={0.75}>
            <path d={d} markerEnd="url(#glyph-arrow)" />
        </g>
    );
}

const Defs = () => (
    <defs>
        <marker id="glyph-arrow" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M1 1 L7 4 L1 7" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
        </marker>
    </defs>
);

export const CameraGlyph: React.FC<{ angle: CameraAngle; className?: string }> = ({ angle, className }) => {
    let body: React.ReactNode;
    switch (angle) {
        case 'wide':
            body = (<><Floor /><Chair x={64} s={0.7} /><rect x={58} y={48} width={30} height={6} rx={1} {...stroke} strokeWidth={1} opacity={0.4} /><Cam x={16} y={32} angle={12} cone={58} spread={22} /></>);
            break;
        case 'eye-level':
            body = (<><Floor /><Chair x={58} /><Cam x={18} y={39} angle={0} cone={36} spread={13} /></>);
            break;
        case 'three-quarter':
            body = (<><Floor /><Chair x={56} /><Cam x={18} y={22} angle={22} cone={38} spread={14} /></>);
            break;
        case 'low':
            body = (<><Floor /><Chair x={58} /><Cam x={16} y={49} angle={-12} cone={40} spread={14} /></>);
            break;
        case 'high':
            body = (<><Floor /><Chair x={58} /><Cam x={22} y={10} angle={42} cone={42} spread={14} /></>);
            break;
        case 'top-down':
            body = (<><Floor />
                {/* the chair seen from above */}
                <g {...stroke}><rect x={36} y={38} width={24} height={14} rx={3} /><path d="M36 40 h24" /><path d="M38 38 v-2 h20 v2" /></g>
                <Cam x={48} y={8} angle={90} cone={26} spread={24} /></>);
            break;
        case 'detail':
            body = (<><Floor /><Chair x={56} s={1.15} />
                <circle cx={64} cy={38} r={7} {...stroke} strokeWidth={1.2} strokeDasharray="2 2" />
                <Cam x={34} y={36} angle={4} cone={22} spread={10} /></>);
            break;
        case 'macro':
            body = (<>
                <circle cx={58} cy={32} r={20} {...stroke} />
                <g {...stroke} strokeWidth={1} opacity={0.6}>
                    {[-12, -6, 0, 6, 12].map((o) => <path key={o} d={`M${44} ${32 + o} q7 -3 14 0 t14 0`} />)}
                </g>
                <line x1={72} y1={46} x2={82} y2={56} {...stroke} strokeWidth={2.4} />
                <Cam x={18} y={32} angle={0} cone={20} spread={18} /></>);
            break;
        case 'over-shoulder':
            body = (<><Floor /><Chair x={64} s={0.85} />
                <g {...stroke}><circle cx={30} cy={26} r={7} fill="var(--glyph-bg, white)" /><path d="M16 54 c0 -14 6 -20 14 -20 s14 6 14 20" fill="var(--glyph-bg, white)" /></g>
                <Cam x={12} y={20} angle={14} cone={66} spread={10} /></>);
            break;
        default:
            body = <Floor />;
    }
    return <svg viewBox={`0 0 ${W} ${H}`} className={className} aria-hidden><Defs />{body}</svg>;
};

export const MoveGlyph: React.FC<{ move: CameraMove; className?: string }> = ({ move, className }) => {
    let body: React.ReactNode;
    switch (move) {
        case 'static':
            body = (<><Floor /><Chair x={60} /><g {...stroke} strokeWidth={1.2} opacity={0.6}><path d="M22 44 l-5 10 M22 44 l5 10 M22 44 v10" /></g><Cam x={22} y={37} angle={0} cone={30} spread={13} /></>);
            break;
        case 'push-in':
            body = (<><Floor /><Chair x={62} /><Arrow d="M18 22 H44" /><g className="move-push"><Cam x={18} y={38} angle={0} cone={30} spread={13} /></g></>);
            break;
        case 'pull-out':
            body = (<><Floor /><Chair x={62} /><Arrow d="M44 22 H18" /><g className="move-pull"><Cam x={40} y={38} angle={0} cone={16} spread={16} /></g></>);
            break;
        case 'pan':
            body = (<><Floor /><Chair x={62} /><Arrow d="M30 16 A16 16 0 0 1 30 52" /><g className="move-pan" style={{ transformOrigin: '20px 34px' }}><Cam x={20} y={34} angle={-10} cone={34} spread={13} /></g></>);
            break;
        case 'orbit':
            body = (<><Floor /><Chair x={36} /><ellipse cx={50} cy={46} rx={36} ry={9} {...stroke} strokeWidth={1.2} strokeDasharray="3 3" opacity={0.6} /><g className="move-orbit"><Cam x={84} y={42} angle={200} cone={22} spread={13} /></g></>);
            break;
        case 'tilt':
            body = (<><Floor /><Chair x={60} /><Arrow d="M12 46 A18 18 0 0 1 12 18" /><g className="move-tilt" style={{ transformOrigin: '22px 38px' }}><Cam x={22} y={38} angle={8} cone={32} spread={13} /></g></>);
            break;
        case 'crane-down':
            body = (<><Floor /><Chair x={62} /><Arrow d="M14 8 V34" /><g className="move-crane"><Cam x={26} y={12} angle={30} cone={30} spread={13} /></g></>);
            break;
        case 'tracking':
            body = (<><Floor /><circle cx={52} cy={26} r={4} {...stroke} /><path d="M52 30 v14 l-5 10 M52 44 l5 10 M46 36 h12" {...stroke} /><Arrow d="M20 60 H78" /><g className="move-track"><Cam x={22} y={34} angle={-4} cone={30} spread={13} /></g></>);
            break;
        case 'handheld':
            body = (<><Floor /><Chair x={60} /><g className="move-shake"><Cam x={24} y={34} angle={2} cone={32} spread={13} /></g><path d="M10 22 q3 3 0 6 M10 40 q3 3 0 6" {...stroke} strokeWidth={1.2} opacity={0.6} /></>);
            break;
        default:
            body = <Floor />;
    }
    return <svg viewBox={`0 0 ${W} ${H}`} className={className} aria-hidden><Defs />{body}</svg>;
};

/** Camera travel for the move glyphs, played while hovered or chosen. */
export const GLYPH_STYLES = `
.glyph-tile .move-push, .glyph-tile .move-pull, .glyph-tile .move-pan, .glyph-tile .move-orbit,
.glyph-tile .move-tilt, .glyph-tile .move-crane, .glyph-tile .move-track, .glyph-tile .move-shake { transform-box: view-box; }
.glyph-tile:hover .move-push, .glyph-tile[aria-checked="true"] .move-push { animation: g-push 1.8s ease-in-out infinite alternate; }
.glyph-tile:hover .move-pull, .glyph-tile[aria-checked="true"] .move-pull { animation: g-pull 1.8s ease-in-out infinite alternate; }
.glyph-tile:hover .move-pan, .glyph-tile[aria-checked="true"] .move-pan { animation: g-pan 1.8s ease-in-out infinite alternate; }
.glyph-tile:hover .move-orbit, .glyph-tile[aria-checked="true"] .move-orbit { animation: g-orbit 2.4s ease-in-out infinite alternate; }
.glyph-tile:hover .move-tilt, .glyph-tile[aria-checked="true"] .move-tilt { animation: g-tilt 1.8s ease-in-out infinite alternate; }
.glyph-tile:hover .move-crane, .glyph-tile[aria-checked="true"] .move-crane { animation: g-crane 1.8s ease-in-out infinite alternate; }
.glyph-tile:hover .move-track, .glyph-tile[aria-checked="true"] .move-track { animation: g-track 2s ease-in-out infinite alternate; }
.glyph-tile:hover .move-shake, .glyph-tile[aria-checked="true"] .move-shake { animation: g-shake 0.9s ease-in-out infinite; }
@keyframes g-push { to { transform: translateX(20px); } }
@keyframes g-pull { to { transform: translateX(-20px); } }
@keyframes g-pan { from { transform: rotate(-14deg); } to { transform: rotate(18deg); } }
@keyframes g-orbit { from { transform: translate(0, 0); } to { transform: translate(-70px, 6px); } }
@keyframes g-tilt { from { transform: rotate(14deg); } to { transform: rotate(-26deg); } }
@keyframes g-crane { to { transform: translate(-4px, 26px) rotate(-14deg); } }
@keyframes g-track { to { transform: translateX(40px); } }
@keyframes g-shake { 0%,100% { transform: translate(0,0) rotate(0); } 25% { transform: translate(1px,-1px) rotate(1deg); } 50% { transform: translate(-1px,1px) rotate(-1deg); } 75% { transform: translate(1px,1px) rotate(0.5deg); } }
@media (prefers-reduced-motion: reduce) { .glyph-tile * { animation: none !important; } }
`;
